import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Button, CircularProgress, Tooltip } from "@mui/material";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/enrollmentAdvice.scss";

const darkTheme = {
  background: "#111827",
  paper: "#1f2937",
  border: "#374151",
  text: "#f9fafb",
  subText: "#9ca3af",
};

const getMonthValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

// デフォルトは「今月を含む過去3ヶ月」: 先々月〜当月
const getDefaultStartMonth = () => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 2);
  return getMonthValue(date);
};

const getDefaultEndMonth = () => getMonthValue(new Date());

// 月セレクタの最大値（当月まで）
const getCurrentMonth = () => getMonthValue(new Date());

const buildMonthOptions = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const [startY, startM] = startDate.split("-").map(Number);
  const [endY, endM] = endDate.split("-").map(Number);
  if (!startY || !startM || !endY || !endM) return [];
  const options = [];
  const cursor = new Date(startY, startM - 1, 1);
  const end = new Date(endY, endM - 1, 1);
  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    options.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return options;
};

const formatRate = (rate) => `${((rate ?? 0) * 100).toFixed(1)}%`;

// 体験人数（棒）と入会率（折れ線）のスパークライン
const TrendSparkline = ({ trend, width = 160, height = 44 }) => {
  if (!trend || trend.length === 0) {
    return <span style={{ color: "#6b7280", fontSize: 11 }}>—</span>;
  }
  const n = trend.length;
  const padTop = 4;
  const padBottom = 4;
  const innerH = height - padTop - padBottom;
  const maxTrials = Math.max(1, ...trend.map((t) => t.total_unique_trials || 0));
  const barW = width / n;

  const linePoints = trend
    .map((t, i) => {
      const x = i * barW + barW / 2;
      const y = padTop + (1 - (t.conversion_rate || 0)) * innerH;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      {/* 0/100% guide lines */}
      <line x1="0" y1={padTop} x2={width} y2={padTop} stroke="#374151" strokeWidth="1" strokeDasharray="2 3" />
      <line x1="0" y1={height - padBottom} x2={width} y2={height - padBottom} stroke="#374151" strokeWidth="1" strokeDasharray="2 3" />
      {/* Trial count bars */}
      {trend.map((t, i) => {
        const h = ((t.total_unique_trials || 0) / maxTrials) * innerH;
        return (
          <rect
            key={i}
            x={i * barW + 2}
            y={height - padBottom - h}
            width={Math.max(1, barW - 4)}
            height={h}
            fill="#38bdf8"
            opacity="0.55"
          />
        );
      })}
      {/* Conversion rate line */}
      <polyline points={linePoints} fill="none" stroke="#facc15" strokeWidth="1.5" />
      {trend.map((t, i) => {
        const x = i * barW + barW / 2;
        const y = padTop + (1 - (t.conversion_rate || 0)) * innerH;
        return <circle key={`c${i}`} cx={x} cy={y} r="2" fill="#facc15" />;
      })}
    </svg>
  );
};

function EnrollmentAdvicePage() {
  useLoginCheck();

  const minMonth = "2022-09";
  const maxMonth = getCurrentMonth();
  const defaultStart = getDefaultStartMonth();
  const initialStart = defaultStart < minMonth ? minMonth : defaultStart > maxMonth ? maxMonth : defaultStart;
  const initialEnd = maxMonth < initialStart ? initialStart : maxMonth;
  const monthOptions = buildMonthOptions(`${minMonth}-01`, `${maxMonth}-01`);

  const [rows, setRows] = useState([]);
  const [months, setMonths] = useState([]);
  const [adviceDrafts, setAdviceDrafts] = useState({});
  const [karteDrafts, setKarteDrafts] = useState({});
  const [busyAi, setBusyAi] = useState({});
  const [dirty, setDirty] = useState({}); // {[instructor_id]: true} 未保存フラグ
  const [saving, setSaving] = useState({}); // {[instructor_id]: true} 保存中フラグ
  const [autoSaveError, setAutoSaveError] = useState(false);
  const [aiCandidates, setAiCandidates] = useState({}); // {[instructor_id]: 生成文（未採用）}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rangeError, setRangeError] = useState("");
  const [startMonth, setStartMonth] = useState(initialStart);
  const [endMonth, setEndMonth] = useState(initialEnd);
  const [appliedRange, setAppliedRange] = useState({ start: initialStart, end: initialEnd });
  const [instruction, setInstruction] = useState("");
  const [busyInstructionSave, setBusyInstructionSave] = useState(false);
  const [instructionSavedFlash, setInstructionSavedFlash] = useState(false);

  // ドラフトの最新値を ref に保持（保存関数を安定化させるため）
  const adviceDraftsRef = useRef({});
  const karteDraftsRef = useRef({});
  useEffect(() => { adviceDraftsRef.current = adviceDrafts; }, [adviceDrafts]);
  useEffect(() => { karteDraftsRef.current = karteDrafts; }, [karteDrafts]);

  // 行ごとのデバウンスタイマー
  const debounceTimers = useRef({});

  // アンマウント時にすべてのタイマーをクリア
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach((t) => clearTimeout(t));
      debounceTimers.current = {};
    };
  }, []);

  const fetchAdvice = useCallback(async (s, e, signal) => {
    setLoading(true);
    setError(null);
    setRangeError("");
    try {
      if (!s || !e) {
        setRangeError("開始月と終了月を選択してください。");
        return;
      }
      if (s > e) {
        setRangeError("開始月が終了月より後になっています。");
        return;
      }
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(
        `${apiUrl}/api/get_enrollment_advice/?start_month=${s}&end_month=${e}`,
        { signal }
      );
      if (!res.ok) throw new Error("ネットワークエラー");
      const json = await res.json();
      const instructors = json.instructors || [];
      setRows(instructors);
      setMonths(json.months || []);
      setInstruction(json.instruction || "");
      const seededAdvice = {};
      const seededKarte = {};
      instructors.forEach((r) => {
        seededAdvice[r.instructor_id] = r.advice || "";
        seededKarte[r.instructor_id] = r.karte || "";
      });
      setAdviceDrafts(seededAdvice);
      setKarteDrafts(seededKarte);
      adviceDraftsRef.current = seededAdvice;
      karteDraftsRef.current = seededKarte;
      setAppliedRange({ start: s, end: e });
      setDirty({});
      setSaving({});
      setAutoSaveError(false);
      setAiCandidates({});
      // 既存のデバウンスタイマーをクリア
      Object.values(debounceTimers.current).forEach((t) => clearTimeout(t));
      debounceTimers.current = {};
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("入会率アドバイスの取得エラー:", err);
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchAdvice(startMonth, endMonth, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    const controller = new AbortController();
    fetchAdvice(startMonth, endMonth, controller.signal);
  };

  const handleSaveInstruction = async () => {
    setBusyInstructionSave(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/save_enrollment_advice_instruction/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instruction }),
      });
      const json = await res.json();
      if (json.ok) {
        setInstructionSavedFlash(true);
        setTimeout(() => setInstructionSavedFlash(false), 2000);
      } else {
        alert(`指示の保存に失敗しました: ${json.message || ""}`);
      }
    } catch (err) {
      alert(`指示の保存に失敗しました: ${err.message}`);
    } finally {
      setBusyInstructionSave(false);
    }
  };

  const handleAiGenerate = async (row) => {
    setBusyAi((b) => ({ ...b, [row.instructor_id]: true }));
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/generate_enrollment_advice/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instructor: row.instructor,
          trend: row.trend,
          start_month: appliedRange.start,
          end_month: appliedRange.end,
          instruction,
          current_advice: adviceDraftsRef.current[row.instructor_id] || "",
          lessons: row.lessons || [],
          lesson_count: row.lesson_count || 0,
          total_students: row.total_students || 0,
          avg_students: row.avg_students || 0,
        }),
      });
      const json = await res.json();
      if (json.advice) {
        // 即反映せず、候補として保持。ユーザーが「採用」を押すまでテキストエリアは変更しない
        setAiCandidates((c) => ({ ...c, [row.instructor_id]: json.advice }));
      } else {
        alert(`AI分析に失敗しました: ${json.error || "原因不明"}`);
      }
    } catch (err) {
      alert(`AI分析に失敗しました: ${err.message}`);
    } finally {
      setBusyAi((b) => ({ ...b, [row.instructor_id]: false }));
    }
  };

  const acceptAiCandidate = (id) => {
    const candidate = aiCandidates[id];
    if (candidate === undefined) return;
    setAdviceDrafts((d) => ({ ...d, [id]: candidate }));
    adviceDraftsRef.current = { ...adviceDraftsRef.current, [id]: candidate };
    setAiCandidates((c) => {
      const n = { ...c };
      delete n[id];
      return n;
    });
    setDirty((d) => ({ ...d, [id]: true }));
    const t = debounceTimers.current[id];
    if (t) {
      clearTimeout(t);
      delete debounceTimers.current[id];
    }
    saveRow(id);
  };

  const discardAiCandidate = (id) => {
    setAiCandidates((c) => {
      const n = { ...c };
      delete n[id];
      return n;
    });
  };

  // 単一行の保存（ref から最新ドラフトを読む）
  const saveRow = useCallback(async (id) => {
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/save_enrollment_advice/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instructor_id: id,
          advice: adviceDraftsRef.current[id] || "",
          karte: karteDraftsRef.current[id] || "",
        }),
      });
      const json = await res.json();
      if (json.ok) {
        setDirty((d) => {
          const n = { ...d };
          delete n[id];
          return n;
        });
        setAutoSaveError(false);
        return true;
      }
      setAutoSaveError(true);
      return false;
    } catch (err) {
      console.error("自動保存エラー:", err);
      setAutoSaveError(true);
      return false;
    } finally {
      setSaving((s) => {
        const n = { ...s };
        delete n[id];
        return n;
      });
    }
  }, []);

  // 編集時のデバウンス自動保存（1.5秒）
  const scheduleAutoSave = useCallback((id) => {
    setDirty((d) => ({ ...d, [id]: true }));
    if (debounceTimers.current[id]) clearTimeout(debounceTimers.current[id]);
    debounceTimers.current[id] = setTimeout(() => {
      delete debounceTimers.current[id];
      saveRow(id);
    }, 1500);
  }, [saveRow]);

  // 全て保存（待機中のデバウンスをフラッシュして並列保存）
  const handleSaveAll = useCallback(async () => {
    const ids = Object.keys(dirty);
    ids.forEach((id) => {
      const t = debounceTimers.current[id];
      if (t) {
        clearTimeout(t);
        delete debounceTimers.current[id];
      }
    });
    await Promise.all(ids.map((id) => saveRow(id)));
  }, [dirty, saveRow]);

  if (loading) {
    return (
      <Box sx={{ bgcolor: darkTheme.background, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: darkTheme.subText }}>読み込み中...</Typography>
      </Box>
    );
  }

  const hasMonthOptions = monthOptions.length > 0;
  const rangeLabel = `${appliedRange.start} 〜 ${appliedRange.end}`;

  return (
    <Box className="enrollment-advice-page" sx={{ bgcolor: darkTheme.background, minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ color: darkTheme.text }}>
          入会率アドバイス
        </Typography>
        <Typography sx={{ color: darkTheme.subText, mt: 0.5, fontSize: 13 }}>
          対象期間: {rangeLabel}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>開始月</Typography>
          <select
            value={startMonth}
            onChange={(e) => {
              const next = e.target.value;
              setStartMonth(next);
              if (endMonth < next) setEndMonth(next);
            }}
            className="ea-month-select"
            disabled={!hasMonthOptions}
          >
            {!hasMonthOptions && <option value="">—</option>}
            {monthOptions.map((m) => (
              <option value={m} key={m} disabled={endMonth && m > endMonth}>
                {m}
              </option>
            ))}
          </select>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>終了月</Typography>
          <select
            value={endMonth}
            onChange={(e) => {
              const next = e.target.value;
              setEndMonth(next);
              if (startMonth > next) setStartMonth(next);
            }}
            className="ea-month-select"
            disabled={!hasMonthOptions}
          >
            {!hasMonthOptions && <option value="">—</option>}
            {monthOptions.map((m) => (
              <option value={m} key={m} disabled={startMonth && m < startMonth}>
                {m}
              </option>
            ))}
          </select>
        </Box>
        <Button
          variant="outlined"
          onClick={handleApply}
          disabled={loading || !hasMonthOptions}
          sx={{
            borderColor: "#38bdf8",
            color: "#e2e8f0",
            fontWeight: 600,
            "&:hover": { borderColor: "#7dd3fc", background: "rgba(56,189,248,0.08)" },
          }}
        >
          絞り込み
        </Button>
        <Box sx={{ display: "flex", gap: 1.5, fontSize: 11, color: darkTheme.subText, alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: "#38bdf8", opacity: 0.55 }} /> 体験人数
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 14, height: 2, bgcolor: "#facc15" }} /> 入会率
          </Box>
        </Box>
      </Box>

      <Box className="ea-instruction-section">
        <Box className="ea-instruction-header">
          <Typography sx={{ color: darkTheme.text, fontSize: 13, fontWeight: 600 }}>
            AI分析への共通指示
          </Typography>
          <Typography sx={{ color: darkTheme.subText, fontSize: 11 }}>
            ここに書いた内容は全インストラクターのAI分析時に渡されます
          </Typography>
        </Box>
        <textarea
          className="ea-instruction-textarea"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="例：今月は新人インストラクターの育成に重点を置いてアドバイスして欲しい / 厳しめに / 入会率より体験数の伸びを評価して"
        />
        <Box className="ea-instruction-actions">
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveInstruction}
            disabled={busyInstructionSave}
            sx={{
              bgcolor: "#0ea5e9",
              color: "#0b1020",
              fontWeight: 700,
              "&:hover": { bgcolor: "#0284c7" },
            }}
          >
            {busyInstructionSave ? <CircularProgress size={14} sx={{ color: "#0b1020" }} /> : "指示を保存"}
          </Button>
          {instructionSavedFlash && <span className="ea-saved-flash">保存済み</span>}
        </Box>
      </Box>

      {rangeError && <Typography sx={{ color: "#fca5a5", mb: 2 }}>{rangeError}</Typography>}
      {error && <Typography sx={{ color: "#fca5a5", mb: 2 }}>データの取得に失敗しました。</Typography>}

      {rows.length === 0 ? (
        <Typography sx={{ color: darkTheme.subText }}>アクティブなインストラクターがいません。</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <div className="ea-table" role="table">
            <div className="ea-row ea-row--header" role="row">
              <div className="col-name" role="columnheader">インストラクター</div>
              <div className="col-advice" role="columnheader">アドバイス</div>
              <div className="col-trend" role="columnheader">体験・入会率の推移</div>
              <div className="col-karte" role="columnheader">カルテ</div>
            </div>
            {rows.map((row) => {
              const id = row.instructor_id;
              const hasTrials = (row.period_total_trials ?? 0) > 0;
              const aiDisabled = busyAi[id] || !hasTrials;
              const lastMonth = months.length > 0 ? months[months.length - 1] : null;
              const lastPoint = row.trend && row.trend.length > 0 ? row.trend[row.trend.length - 1] : null;
              return (
                <div key={id} className="ea-row" role="row">
                  <div className="col-name" role="cell">
                    <div className="col-name-main">{row.instructor}</div>
                    <div className="col-name-sub">
                      期間計: 体験 {(row.period_total_trials ?? 0).toLocaleString()}人 (新規 {(row.period_new_trialers ?? 0).toLocaleString()}人) / 入会率 {formatRate(row.period_conversion_rate)}
                    </div>
                    <div className="col-name-sub">
                      {(row.lesson_count ?? 0) > 0
                        ? `担当 ${row.lesson_count}本 / 平均 ${(row.avg_students ?? 0).toFixed(1)}人 (合計 ${row.total_students ?? 0}人)`
                        : "担当レッスンなし"}
                    </div>
                  </div>
                  <div className="col-advice" role="cell">
                    <textarea
                      className="ea-advice-textarea"
                      value={adviceDrafts[id] ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAdviceDrafts((d) => ({ ...d, [id]: v }));
                        scheduleAutoSave(id);
                      }}
                      placeholder="AI分析で下書きを生成、または手動入力"
                    />
                    <Box className="ea-advice-actions">
                      <Tooltip
                        title={!hasTrials ? "対象期間に体験者がいません" : ""}
                        disableHoverListener={hasTrials}
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleAiGenerate(row)}
                            disabled={aiDisabled}
                            sx={{
                              borderColor: "#a78bfa",
                              color: "#c4b5fd",
                              "&:hover": { borderColor: "#c4b5fd", background: "rgba(167,139,250,0.08)" },
                            }}
                          >
                            {busyAi[id] ? <CircularProgress size={14} sx={{ color: "#c4b5fd" }} /> : "AI分析"}
                          </Button>
                        </span>
                      </Tooltip>
                      {dirty[id] && !saving[id] && <span className="ea-row-state ea-row-state--dirty">未保存</span>}
                      {saving[id] && <span className="ea-row-state ea-row-state--saving">保存中…</span>}
                    </Box>
                    {aiCandidates[id] !== undefined && (
                      <Box className="ea-ai-candidate">
                        <Box className="ea-ai-candidate-header">
                          <span className="ea-ai-candidate-label">AI生成案（未確定）</span>
                          <span className="ea-ai-candidate-hint">採用するか確認してください</span>
                        </Box>
                        <pre className="ea-ai-candidate-body">{aiCandidates[id]}</pre>
                        <Box className="ea-ai-candidate-actions">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => acceptAiCandidate(id)}
                            sx={{
                              bgcolor: "#22c55e",
                              color: "#0b1020",
                              fontWeight: 700,
                              "&:hover": { bgcolor: "#16a34a" },
                            }}
                          >
                            採用
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => discardAiCandidate(id)}
                            sx={{
                              borderColor: "#64748b",
                              color: "#cbd5e1",
                              "&:hover": { borderColor: "#94a3b8", background: "rgba(148,163,184,0.08)" },
                            }}
                          >
                            破棄
                          </Button>
                          <Tooltip
                            title={!hasTrials ? "対象期間に体験者がいません" : ""}
                            disableHoverListener={hasTrials}
                          >
                            <span>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleAiGenerate(row)}
                                disabled={aiDisabled}
                                sx={{ color: "#c4b5fd", "&:hover": { background: "rgba(167,139,250,0.08)" } }}
                              >
                                {busyAi[id] ? <CircularProgress size={14} sx={{ color: "#c4b5fd" }} /> : "再生成"}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                  </div>
                  <div className="col-trend" role="cell">
                    <TrendSparkline trend={row.trend} />
                    <div className="ea-trend-meta">
                      {lastPoint ? (
                        <>
                          <span>{lastMonth}</span>
                          <span>{(lastPoint.total_unique_trials ?? 0).toLocaleString()}人</span>
                          <span>{formatRate(lastPoint.conversion_rate)}</span>
                        </>
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                  </div>
                  <div className="col-karte" role="cell">
                    <textarea
                      className="ea-karte-textarea"
                      value={karteDrafts[id] ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setKarteDrafts((d) => ({ ...d, [id]: v }));
                        scheduleAutoSave(id);
                      }}
                      placeholder="メモを記録（履歴やフォロー内容など）"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Box>
      )}

      <Box className="ea-savebar">
        <span className="ea-savebar-status">
          {(() => {
            const dirtyCount = Object.keys(dirty).length;
            const savingCount = Object.keys(saving).length;
            if (savingCount > 0) return `保存中… (${savingCount} 件)`;
            if (dirtyCount > 0) return `未保存: ${dirtyCount} 件`;
            if (autoSaveError) return "保存エラー — 「全て保存」をお試しください";
            return "✓ 全て保存済み";
          })()}
        </span>
        <Button
          variant="contained"
          onClick={handleSaveAll}
          disabled={Object.keys(dirty).length === 0 || Object.keys(saving).length > 0}
          sx={{
            bgcolor: "#0ea5e9",
            color: "#0b1020",
            fontWeight: 700,
            "&:hover": { bgcolor: "#0284c7" },
            "&.Mui-disabled": { bgcolor: "rgba(14,165,233,0.25)", color: "rgba(11,16,32,0.6)" },
          }}
        >
          全て保存
        </Button>
      </Box>
    </Box>
  );
}

export default EnrollmentAdvicePage;
